/**
 * B1 / AP-5 (P21) — „Meine Favoriten". Dieselbe Tabelle wie „Meine Dateien",
 * gefiltert auf die per-User favorisierten Dateien (D-110). Öffnet über dieselbe
 * route-by-kind-Weiche; der ★-Button entfernt den Favoriten wieder.
 *
 * Favoriten sind per natürlichem User, unabhängig vom Scope — die Ansicht zeigt
 * die favorisierten Dateien des aktiven Workspace (guard-gefiltert serverseitig).
 */

import { useCallback, useEffect, useState } from "react";

import { ApiError, ryfeApi } from "../api";
import { routeForKind } from "../open-file";
import { useRyfe } from "../RyfeContext";
import { navigate } from "../router";

import "./ryfe.scss";

import type { FileMeta } from "../api";

const KIND_LABEL: Record<string, string> = {
  board: "Whiteboard",
  md: "Notiz",
  list: "Liste",
  image: "Bild",
};

const fmtDate = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString() : "—";

export const RyfeFavorites = () => {
  const { workspace } = useRyfe();
  const wsId = workspace?.id;
  const [files, setFiles] = useState<FileMeta[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!wsId) {
      return;
    }
    try {
      const r = await ryfeApi.listFavorites(wsId);
      setFiles(r.files);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Laden fehlgeschlagen");
    }
  }, [wsId]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (f: FileMeta) => {
    const route = routeForKind(f.kind, f.id);
    if (route) {
      navigate(route);
    }
  };

  const unfavorite = async (f: FileMeta) => {
    if (!wsId) {
      return;
    }
    try {
      await ryfeApi.removeFavorite(wsId, f.id);
      setFiles((prev) => (prev ? prev.filter((x) => x.id !== f.id) : prev));
    } catch {
      /* leave the row — removal failed */
    }
  };

  return (
    <div className="ryfe-page">
      <h1 className="ryfe-page__title">Meine Favoriten</h1>

      {error && <div className="ryfe-notice ryfe-notice--warn">{error}</div>}

      {files && files.length === 0 ? (
        <p className="ryfe-muted" style={{ padding: 0 }}>
          Noch keine Favoriten. Markiere eine Datei über „★ Favorit" in „Meine
          Dateien".
        </p>
      ) : (
        <div className="ryfe-table-wrap">
          <table className="ryfe-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Typ</th>
                <th>Geändert</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(files || []).map((f) => (
                <tr className="ryfe-row" key={f.id}>
                  <td>
                    <button className="ryfe-btn--link" onClick={() => open(f)}>
                      {f.name}
                    </button>
                  </td>
                  <td>{KIND_LABEL[f.kind] || f.kind}</td>
                  <td>{fmtDate(f.updated_at || f.created_at)}</td>
                  <td>
                    <button
                      className="ryfe-star ryfe-star--on"
                      title="Aus Favoriten entfernen"
                      aria-label="Aus Favoriten entfernen"
                      onClick={() => unfavorite(f)}
                    >
                      ★
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
